import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { AudioPlayerService } from '../../../../core/services/audio-player.service';
import { TranslocoPipe } from '@jsverse/transloco';

/**
 * Persistent audio player — compact floating pill, bottom-left.
 * Visible whenever audio is playing or paused.
 * Survives route navigation (rendered in app shell).
 */
@Component({
  selector: 'im-audio-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, TranslocoPipe],
  template: `
    @if (player.visible()) {
      <div class="ab-bar" role="region" [attr.aria-label]="'shell.audio_bar.audio_player' | transloco">
        <!-- Play/Pause -->
        <button class="ab-play" (click)="player.toggle()"
                [attr.aria-label]="player.playing() ? 'Pause' : 'Play'"
                [attr.aria-pressed]="player.playing()">
          @if (player.loading()) {
            <div class="ab-spinner"></div>
          } @else {
            <paper-icon [name]="player.playing() ? 'pause' : 'play'" [size]="18" />
          }
        </button>

        <!-- Info -->
        <div class="ab-body">
          <span class="ab-title">{{ player.title() }}</span>
          <span class="ab-time">{{ player.formatTime(player.currentTime()) }} / {{ player.formatTime(player.duration()) }}</span>
        </div>

        <!-- Speed -->
        <button class="ab-speed" (click)="player.cycleSpeed()"
                [attr.aria-label]="'Speed ' + player.playbackRate() + 'x'">
          {{ player.playbackRate() }}x
        </button>

        <!-- Close -->
        <button class="ab-close" (click)="player.close()" [attr.aria-label]="'shell.audio_bar.close' | transloco">
          <paper-icon name="x" [size]="14" />
        </button>

        <!-- Full-width progress track at bottom -->
        <div class="ab-track" (click)="onTrackClick($event)">
          <div class="ab-fill" [style.width.%]="player.progress()"></div>
        </div>
      </div>
    }
  `,
  styles: `
    .ab-bar {
      position: fixed;
      bottom: 24px;
      left: 24px;
      z-index: 50;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px 18px;
      background: var(--card-bg-solid);
      border: 1px solid var(--border-low);
      border-radius: var(--radius, 16px);
      box-shadow: var(--panel-shadow);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      min-width: 320px;
      max-width: 420px;
      animation: abFloat 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .ab-play {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: var(--color-primary);
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: var(--text-on-accent);
      padding: 0;
      flex-shrink: 0;
      transition: filter 0.15s ease;

      &:hover { filter: brightness(1.15); }
    }

    .ab-spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: var(--text-on-accent);
      border-radius: 50%;
      animation: abSpin 0.8s linear infinite;
    }

    .ab-body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .ab-title {
      font-size: 13px;
      font-weight: 600;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ab-time {
      font-size: 11px;
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
    }

    .ab-track {
      position: absolute;
      bottom: 0;
      left: 12px;
      right: 12px;
      height: 4px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 0 0 var(--radius, 16px) var(--radius, 16px);
      cursor: pointer;
      overflow: hidden;
      transition: height 0.15s ease;

      &:hover { height: 6px; }
    }

    .ab-fill {
      height: 100%;
      background: var(--color-primary);
      border-radius: inherit;
      transition: width 200ms linear;
    }

    .ab-speed {
      padding: 4px 8px;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm, 8px);
      cursor: pointer;
      font-family: inherit;
      font-size: 11px;
      font-weight: 600;
      color: var(--text-secondary);
      flex-shrink: 0;
      transition: all 0.15s ease;

      &:hover { border-color: var(--border-med); color: var(--text); background: rgba(255, 255, 255, 0.1); }
    }

    .ab-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: none;
      border: 1px solid transparent;
      border-radius: 50%;
      cursor: pointer;
      color: var(--text-tertiary);
      padding: 0;
      flex-shrink: 0;
      transition: all 0.15s ease;

      &:hover { background: rgba(255, 255, 255, 0.06); border-color: var(--border-low); color: var(--text); }
    }

    @keyframes abFloat {
      from { opacity: 0; transform: translateY(12px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes abSpin {
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .ab-bar { animation: none; }
      .ab-spinner { animation-duration: 1.5s; }
      .ab-fill { transition: none; }
    }
  `,
})
export class AudioBarComponent {
  protected readonly player = inject(AudioPlayerService);

  onTrackClick(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const percent = ((event.clientX - rect.left) / rect.width) * 100;
    this.player.seek(Math.max(0, Math.min(100, percent)));
  }
}
